import SearchIcon from '@mui/icons-material/Search';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  ORGANIZATION_FEATURES,
  PAYMENT_METHODS,
  REFUND_STATUSES,
  REFUND_SUB_STATUS,
  REFUND_TYPES,
  TRANSACTION_STATUSES,
  checkOrganizationFeatures,
} from '@/utility';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import PayeeCategorySelect from '@/components/PayeeCategorySelect';
import CustomPaymentTypeSelect from '@/components/CustomPaymentTypeSelect';
import FacilitySelect from '@/components/FacilitySelect';
import CheckboxDropdown from '@/components/CheckboxDropdown/CheckboxDropdown';
import Button from '../../../components/CustomButtons/Button';
import { RefundsApi } from '../../../api/clients/refunds/refunds.api';
import { DataMappingsApi } from '../../../api/clients/data-mappings/data-mappings.api';
import { getBusinessUnitsForForm } from '@/api/clients/business-units/business-units.api';
import { useGetFacilityGroupsForForms } from '@/api/hooks/facility-groups/useGetFacilityGroupsForForms';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';

const COMMUNICATION_EXCEPTIONS = Object.freeze({
  INVALID_ADDRESS: 'Invalid Address',
  UNDELIVERABLE: 'Undeliverable',
});

const checkStatusOptions = [
  TRANSACTION_STATUSES.PENDING,
  TRANSACTION_STATUSES.SENT_UNCASHED,
  TRANSACTION_STATUSES.VOIDED,
];
const achStatusOptions = [
  TRANSACTION_STATUSES.PENDING,
  TRANSACTION_STATUSES.ACH_SENT,
  TRANSACTION_STATUSES.ACH_RETURNED,
  TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK,
  TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM,
];
const tangoStatusOptions = [TRANSACTION_STATUSES.PENDING];

const getQuickFilterDefaults = (quickFilterValue) => {
  if (!quickFilterValue) return {};
  if (quickFilterValue === 'Flagged Records') {
    return {
      quickFilter: quickFilterValue,
      isError: true,
      hasFilesAttached: false,
    };
  }
  if (quickFilterValue === 'All Returned') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.REQUESTED,
      transactionStatus: TRANSACTION_STATUSES.ACH_RETURNED,
      paymentMethod: '7',
      subStatus: '',
      payeeCategory: '',
      additionalInfo: '',
      hasFilesAttached: false,
    };
  }
  if (quickFilterValue === 'All Voided') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.REQUESTED,
      transactionStatus: TRANSACTION_STATUSES.VOIDED,
      paymentMethod: '5',
      subStatus: '',
      payeeCategory: '',
      additionalInfo: '',
      hasFilesAttached: false,
    };
  }
  if (quickFilterValue === 'Payee Exceptions') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.EXCEPTION,
      transactionStatus: '',
      subStatus: REFUND_SUB_STATUS.PAYEE_EXCEPTION,
      payeeCategory: 'insurance',
      additionalInfo: '',
      hasFilesAttached: false,
    };
  }
  if (quickFilterValue === 'Payee Exceptions with PDF') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.EXCEPTION,
      transactionStatus: '',
      subStatus: REFUND_SUB_STATUS.PAYEE_EXCEPTION,
      payeeCategory: 'insurance',
      additionalInfo: '',
      hasFilesAttached: true,
    };
  }
  if (quickFilterValue === 'Addenda Missing') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.EXCEPTION,
      transactionStatus: '',
      subStatus: REFUND_SUB_STATUS.ADDENDA_MISSING,
      payeeCategory: 'insurance',
      additionalInfo: '',
      hasFilesAttached: false,
    };
  }
  if (quickFilterValue === 'Invalid Address') {
    return {
      quickFilter: quickFilterValue,
      status: REFUND_STATUSES.EXCEPTION,
      transactionStatus: '',
      subStatus: REFUND_SUB_STATUS.COMMUNICATION,
      payeeCategory: '',
      additionalInfo: COMMUNICATION_EXCEPTIONS.INVALID_ADDRESS,
      hasFilesAttached: false,
    };
  }
  return {};
};

export default function FacilityTransactionFilters({
  role,
  isCustomPaymentTypesEnabled,
  allowedRefundTypes,
  payeeCategory,
  customPaymentTypeId,
  status,
  subStatus,
  additionalInfo,
  paymentMethod,
  transactionStatus,
  facility,
  facilityGroupId,
  selectedBusinessUnit,
  dateFilterType,
  startDate,
  endDate,
  search,
  quickFilter,
  selectedDataMappings,
  hasFilesAttached,
  sortBy,
  setPartialState,
  handleSearch,
}) {
  const { t } = useTranslation();
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();
  const { facilityGroups } = useGetFacilityGroupsForForms({
    enabled: role === 'admin',
  });

  const [businessUnits, setBusinessUnits] = useState([]);
  const [transactionStatuses, setTransactionStatuses] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [dataMappings, setDataMappings] = useState([]);
  const [dataMappingOptions, setDataMappingOptions] = useState([]);

  const isInsuranceEnabled = useMemo(() => {
    return allowedRefundTypes.includes(REFUND_TYPES.INSURANCE);
  }, [allowedRefundTypes]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      const businessUnitsData = await getBusinessUnitsForForm();
      setBusinessUnits(businessUnitsData);
      setFilteredBusinessUnits(businessUnitsData);
    };
    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    const fetchDataMappings = async () => {
      try {
        const response = await DataMappingsApi.getDataMappingsForFilters(role);
        setDataMappings(response.data);
      } catch (error) {
        console.error('Error fetching data mappings:', error);
      }
    };

    fetchDataMappings();
  }, [role]);

  useEffect(() => {
    if (paymentMethod) {
      const transactionStatusesToUse =
        paymentMethod == 7
          ? achStatusOptions
          : paymentMethod == 6
          ? tangoStatusOptions
          : paymentMethod == 4
          ? tangoStatusOptions
          : paymentMethod == 5
          ? checkStatusOptions
          : tangoStatusOptions;

      setTransactionStatuses(transactionStatusesToUse);
    }
  }, [paymentMethod]);

  useEffect(() => {
    const mappings = dataMappings
      ?.filter((dataMapping) =>
        facility && role === 'admin'
          ? dataMapping.facility_id === +facility
          : true,
      )
      .map((mapping) => ({
        value: mapping.id,
        label: mapping.name,
      }));
    setDataMappingOptions(mappings);
  }, [dataMappings, facility, role]);

  const changeDate = (event) => {
    setPartialState({ startDate: event ?? '' });
  };

  const changeEndDate = (event) => {
    setPartialState({ endDate: event ?? '' });
  };

  const onSearchChange = (event) => {
    setPartialState({ search: event.target.value });
    handleSearch(event.target.value);
  };

  const selectStatus = (event) => {
    setPartialState({
      transactionStatus: '',
      subStatus: '',
      status: event.target.value,
      paymentMethod: '',
      quickFilter: '',
    });
  };

  const updatePaymentMethod = (e) => {
    setPartialState({ paymentMethod: e.target.value, quickFilter: '' });
    const transactionStatusesToUse =
      e.target.value == 7
        ? achStatusOptions
        : e.target.value == 6
        ? tangoStatusOptions
        : e.target.value == 4
        ? tangoStatusOptions
        : e.target.value == 5
        ? checkStatusOptions
        : tangoStatusOptions;

    setTransactionStatuses(transactionStatusesToUse);
  };

  const onFacilitySelect = (value) => {
    setPartialState({ facility: value });
    setFilteredBusinessUnits(
      businessUnits.filter((businessUnit) => businessUnit.facility_id == value),
    );
  };

  const onQuickFilterChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setPartialState({
        quickFilter: '',
        status: '',
        transactionStatus: '',
        paymentMethod: '',
        subStatus: '',
        payeeCategory: '',
        additionalInfo: '',
        isError: false,
      });
      return;
    }
    setPartialState(getQuickFilterDefaults(value));
  };

  const downloadFile = useCallback(async () => {
    try {
      const filters = {
        search: search,
        refundMethod: paymentMethod || undefined,
        facility_id: facility,
        refundStatus: status || undefined,
        orderByColumn: sortBy.name,
        orderType: sortBy.order == 1 ? 'ASC' : 'DESC',
        startDate,
        endDate,
        business_unit_id: selectedBusinessUnit,
        additionalInfo,
        isError: quickFilter === 'Flagged Records',
        subStatus,
        dateFilterType,
        transactionStatus,
        refundType: payeeCategory,
        dataMappings: selectedDataMappings?.length
          ? selectedDataMappings
          : undefined,
        hasFilesAttached: hasFilesAttached,
      };
      const result = await RefundsApi.downloadReport(filters);
      fileDownload(result.data, 'RefundReport.csv');
    } catch (err) {
      console.error(err);
    }
  }, [
    search,
    paymentMethod,
    facility,
    status,
    sortBy,
    startDate,
    endDate,
    selectedBusinessUnit,
    additionalInfo,
    quickFilter,
    subStatus,
    dateFilterType,
    transactionStatus,
    payeeCategory,
    selectedDataMappings,
    hasFilesAttached,
  ]);

  const handleClickDownload = useCallback(() => {
    toast.success(t('payments.toast.downloadingReport'), {
      position: 'bottom-right',
      autoClose: 5000,
    });
    downloadFile();
  }, [downloadFile, t]);

  return (
    <>
      <div className='t-header'>
        <div className='t-header-left' style={{ marginTop: 0 }}>
          <div className='custom-search-box'>
            <div
              className='custom-select'
              style={{ marginTop: 0, paddingBottom: 0 }}>
              <select
                id='status'
                className='full-width-elem'
                value={dateFilterType}
                onChange={(e) =>
                  setPartialState({ dateFilterType: e.target.value })
                }>
                <option value='created_at'>
                  {t('payments.dateFilters.dateProcessed')}
                </option>
                <option value='status_updatedat'>
                  {t('payments.dateFilters.lastUpdated')}
                </option>
              </select>
            </div>
            <span className='search-icon'></span>
          </div>
        </div>
        <div className='t-header-left' style={{ marginTop: 0 }}>
          {t('payments.dateFilters.fromLabel')}&nbsp;&nbsp;
          <DatePicker
            className='full-width-elem-date-picker'
            id='dt'
            autoComplete='off'
            selected={startDate}
            onChange={changeDate}
          />
        </div>
        <div className='t-header-left' style={{ marginTop: 0 }}>
          {t('payments.dateFilters.toLabel')}&nbsp;&nbsp;
          <DatePicker
            className='full-width-elem-date-picker'
            id='dt'
            autoComplete='off'
            selected={endDate}
            onChange={changeEndDate}
          />
        </div>

        <div className='t-header-right'>
          <div className='custom-search-box'>
            <input
              type='search'
              className='seacrh-field'
              value={search}
              onChange={onSearchChange}
              placeholder={t('payments.search.placeholder')}
            />
            <span className='search-icon'>
              <SearchIcon />
            </span>
          </div>
        </div>
      </div>
      <div className='t-header'>
        {(role === 'admin' || !isCustomPaymentTypesEnabled) && (
          <div
            className='custom-select'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            {allowedRefundTypes.length > 1 || role === 'admin' ? (
              <PayeeCategorySelect
                disablePadding
                role={role}
                allowedRefundTypes={allowedRefundTypes}
                payeeCategory={payeeCategory}
                onChange={(e) =>
                  setPartialState({
                    payeeCategory: e.target.value,
                    quickFilter: '',
                  })
                }
              />
            ) : null}
          </div>
        )}
        {(role === 'admin' || isCustomPaymentTypesEnabled) && (
          <CustomPaymentTypeSelect
            value={customPaymentTypeId}
            onChange={(e) =>
              setPartialState({
                customPaymentTypeId: e.target.value,
                quickFilter: '',
              })
            }
          />
        )}

        <div
          className='t-header-left'
          style={{ marginTop: 0, paddingBottom: 0 }}>
          <div className='custom-search-box'>
            <div
              className='custom-select'
              style={{ marginTop: 0, paddingBottom: 0 }}>
              <select
                id='status'
                className='full-width-elem'
                value={status}
                onChange={selectStatus}>
                <option value=''>{t('payments.filters.refundStatus')}</option>
                {Object.values(REFUND_STATUSES).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <span className='search-icon'></span>
          </div>
        </div>

        {status === REFUND_STATUSES.UNCLAIMED && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={subStatus}
                  onChange={(e) =>
                    setPartialState({
                      subStatus: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>{t('payments.filters.subStatus')}</option>
                  <option value={REFUND_SUB_STATUS.EMAIL_SENT}>
                    {t('payments.subStatus.emailSent')}
                  </option>
                  <option value={REFUND_SUB_STATUS.COMMUNICATION_PENDING}>
                    {t('payments.subStatus.communicationPending')}
                  </option>
                  <option value={REFUND_SUB_STATUS.MAIL_SENT}>
                    {t('payments.subStatus.mailSent')}
                  </option>
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {status === REFUND_STATUSES.CANCELED && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={subStatus}
                  onChange={(e) =>
                    setPartialState({
                      subStatus: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.subStatusPlaceholder')}
                  </option>
                  <option value={REFUND_SUB_STATUS.TRANSFER_PENDING}>
                    {t('common.subStatus.transferPending')}
                  </option>
                  <option value={REFUND_SUB_STATUS.TRANSFER_COMPLETE}>
                    {t('common.subStatus.transferComplete')}
                  </option>
                  <option value={REFUND_SUB_STATUS.TRANSFER_FAILED}>
                    {t('common.subStatus.transferFailed')}
                  </option>
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {status === REFUND_STATUSES.EXCEPTION && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={subStatus}
                  onChange={(e) =>
                    setPartialState({
                      subStatus: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.subStatusPlaceholder')}
                  </option>
                  <option value={REFUND_SUB_STATUS.COMMUNICATION}>
                    {t('payments.subStatus.communication')}
                  </option>
                  <option value={REFUND_SUB_STATUS.PAYEE_EXCEPTION}>
                    {t('payments.subStatus.payeeException')}
                  </option>
                  <option value={REFUND_SUB_STATUS.ADDENDA_MISSING}>
                    {t('payments.subStatus.addendaMissing')}
                  </option>
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {subStatus === REFUND_SUB_STATUS.COMMUNICATION && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={additionalInfo}
                  onChange={(e) =>
                    setPartialState({
                      additionalInfo: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.subStatusPlaceholder')}
                  </option>
                  {Object.values(COMMUNICATION_EXCEPTIONS).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {status === REFUND_STATUSES.ESCHEATED && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={subStatus}
                  onChange={(e) =>
                    setPartialState({
                      subStatus: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.subStatusPlaceholder')}
                  </option>
                  <option value={REFUND_SUB_STATUS.DORMANT_PENDING_ESCHEATMENT}>
                    {t('payments.subStatus.dormantPendingEscheatment')}
                  </option>
                  <option value={REFUND_SUB_STATUS.ESCHEATMENT_EMAIL_SENT}>
                    {t('payments.subStatus.escheatmentEmailSent')}
                  </option>
                  <option
                    value={REFUND_SUB_STATUS.ESCHEATMENT_PHYSICAL_MAIL_SENT}>
                    {t('payments.subStatus.escheatmentPhysicalMailSent')}
                  </option>
                  <option
                    value={REFUND_SUB_STATUS.ESCHEATMENT_CERTIFIED_MAIL_SENT}>
                    {t('payments.subStatus.escheatmentCertifiedMailSent')}
                  </option>
                  <option
                    value={REFUND_SUB_STATUS.ESCHEATMENT_NUAPA_CLIENT_REVIEW}>
                    {t('payments.subStatus.escheatmentNuapaClientReview')}
                  </option>
                  <option value={REFUND_SUB_STATUS.ESCHEATED_TO_STATE}>
                    {t('payments.subStatus.escheatedToState')}
                  </option>
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {(status === REFUND_STATUSES.COMPLETED ||
          status === REFUND_STATUSES.REQUESTED) && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={paymentMethod}
                  onChange={updatePaymentMethod}>
                  <option value=''>
                    {t('payments.filters.paymentTypePlaceholder')}
                  </option>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.text}
                    </option>
                  ))}
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        {status === REFUND_STATUSES.REQUESTED && paymentMethod && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={transactionStatus}
                  onChange={(e) =>
                    setPartialState({
                      transactionStatus: e.target.value,
                      quickFilter: '',
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.transactionStatusPlaceholder')}
                  </option>
                  {transactionStatuses.map((method) => (
                    <option key={method} value={method}>
                      {getTranslatedStatus(method)}
                    </option>
                  ))}
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}

        <FacilitySelect
          value={facility}
          onChange={onFacilitySelect}
          parentKey='transaction_page'
          childKey='refund_information'
          permission='read'
        />

        {role === 'admin' && (
          <div style={{ marginTop: 0, paddingBottom: 0, marginLeft: 10 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='facilityGroup'
                  className='full-width-elem'
                  value={facilityGroupId}
                  style={{ height: '35px' }}
                  onChange={(e) =>
                    setPartialState({
                      facilityGroupId: e.target.value,
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.facilityGroupPlaceholder')}
                  </option>
                  {facilityGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.organization_name} - {group.name}
                    </option>
                  ))}
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}

        {(checkOrganizationFeatures(
          ORGANIZATION_FEATURES.showBusinessUnitsPage,
        ) ||
          role === 'admin') && (
          <div
            className='t-header-left'
            style={{ marginTop: 0, paddingBottom: 0 }}>
            <div className='custom-search-box'>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <select
                  id='status'
                  className='full-width-elem'
                  value={selectedBusinessUnit}
                  onChange={(e) =>
                    setPartialState({
                      selectedBusinessUnit: e.target.value,
                    })
                  }>
                  <option value=''>
                    {t('payments.filters.businessUnitPlaceholder')}
                  </option>
                  {filteredBusinessUnits?.map((businessUnit) => (
                    <option key={businessUnit.id} value={businessUnit.id}>
                      {businessUnit.name}
                    </option>
                  ))}
                </select>
              </div>
              <span className='search-icon'></span>
            </div>
          </div>
        )}
        <div
          className='t-header-right'
          style={{
            maxWidth: '200px',
          }}>
          <div className='custom-search-box'>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '10px',
                alignItems: 'center',
              }}>
              <div
                className='custom-select'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                {(role === 'admin' ||
                  checkPermission({
                    parentKey: 'transaction_page',
                    childKey: 'refund_information',
                    permission: 'download',
                  })) && (
                  <Button
                    className='full-width-elem'
                    style={{
                      height: '40px',
                    }}
                    onClick={handleClickDownload}
                    color='primary'>
                    {t('payments.buttons.download')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <CheckboxDropdown
          options={dataMappingOptions}
          onChange={(selectedValues) =>
            setPartialState({
              selectedDataMappings: selectedValues.map((item) => item.value),
            })
          }
        />
        <div className='custom-search-box'>
          <div>
            <select
              style={{ width: '200px' }}
              value={quickFilter}
              id='status'
              className='full-width-elem'
              onChange={onQuickFilterChange}>
              <option value=''>{t('payments.quickFilters.placeholder')}</option>
              <option value='All Returned'>
                {t('payments.quickFilters.allReturned')}
              </option>
              <option value='All Voided'>
                {t('payments.quickFilters.allVoided')}
              </option>
              {role === 'admin' && (
                <option value='Flagged Records'>
                  {t('payments.quickFilters.flaggedRecords')}
                </option>
              )}
              {(isInsuranceEnabled || role === 'admin') && (
                <option value='Payee Exceptions'>
                  {t('payments.quickFilters.payeeExceptions')}
                </option>
              )}
              {(isInsuranceEnabled || role === 'admin') && (
                <option value='Payee Exceptions with PDF'>
                  {t('payments.quickFilters.payeeExceptionsWithPdf')}
                </option>
              )}
              {(isInsuranceEnabled || role === 'admin') && (
                <option value='Addenda Missing'>
                  {t('payments.quickFilters.addendaMissing')}
                </option>
              )}
              <option value='Invalid Address'>
                {t('payments.quickFilters.invalidAddress')}
              </option>
            </select>
          </div>
          <span className='search-icon'></span>
        </div>
      </div>
    </>
  );
}
